import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supportTicketManagementService, SupportTicket, SupportTicketWithUser, SupportTicketMessage, ListSupportTicketsParams } from '../services/supportTicketManagement';
import './SupportTicketsPage.css';
import { Table, TableColumn } from '../components/Table';

export const SupportTicketsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const [viewingTicket, setViewingTicket] = useState<string | null>(null);
  const itemsPerPage = 50;

  useEffect(() => {
    const id = searchParams.get('ticketId');
    setViewingTicket(id ?? null);
  }, [searchParams]);

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListSupportTicketsParams = {
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
        priority: priorityFilter !== 'all' ? priorityFilter : undefined,
      };

      if (currentPage > 1 && lastEvaluatedKey) {
        params.lastEvaluatedKey = lastEvaluatedKey;
      }

      const response = await supportTicketManagementService.listSupportTickets(params);
      setTickets(response.tickets);
      setTotalCount(response.count);
      setLastEvaluatedKey(
        response.lastEvaluatedKey
          ? encodeURIComponent(JSON.stringify(response.lastEvaluatedKey))
          : undefined
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load support tickets');
      console.error('Error loading support tickets:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- lastEvaluatedKey intentionally omitted
  }, [currentPage, statusFilter, typeFilter, priorityFilter, searchTerm]);

  const openTicketModal = useCallback(
    (ticketId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('ticketId', ticketId);
      setSearchParams(next, {replace: true});
    },
    [searchParams, setSearchParams]
  );

  const closeTicketModal = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('ticketId');
    setSearchParams(next, {replace: true});
    void loadTickets();
  }, [searchParams, setSearchParams, loadTickets]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
    loadTickets();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setPriorityFilter('all');
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleExport = () => {
    const csvContent = [
      ['Ticket ID', 'User ID', 'Type', 'Subject', 'Status', 'Priority', 'Created', 'Updated'].join(','),
      ...tickets.map(t => [
        t.ticketId,
        t.userId,
        t.type,
        t.subject,
        t.status,
        t.priority,
        t.createdAt,
        t.updatedAt,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `support-tickets-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns: TableColumn<SupportTicket>[] = useMemo(() => [
    {
      header: 'Ticket ID',
      accessor: 'ticketId',
      render: (value) => (
        <div className="ticket-id">{(value as string).substring(0, 8)}...</div>
      ),
    },
    {
      header: 'User ID',
      accessor: 'userId',
      render: (value) => (
        <div className="secondary-text">{(value as string).substring(0, 8)}...</div>
      ),
      hideOnTablet: true,
    },
    {
      header: 'Type',
      accessor: 'type',
      render: (value) => (
        <span className="type-badge">{value as string}</span>
      ),
    },
    {
      header: 'Subject',
      accessor: 'subject',
      render: (value) => (
        <div className="subject-text">{value as string}</div>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (value) => (
        <span className={`status-badge ${value as string}`}>
          {value as string}
        </span>
      ),
    },
    {
      header: 'Priority',
      accessor: 'priority',
      render: (value) => (
        <span className={`priority-badge ${value as string}`}>
          {value as string}
        </span>
      ),
      hideOnTablet: true,
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: (value) => formatDate(value as string),
      hideOnMobile: true,
    },
    {
      header: 'Updated',
      accessor: 'updatedAt',
      render: (value) => formatDate(value as string),
      hideOnMobile: true,
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <button
          type="button"
          className="view-button"
          onClick={() => openTicketModal(row.ticketId)}
        >
          View
        </button>
      ),
      dataLabel: '',
    },
  ], [openTicketModal]);

  return (
    <div className="support-tickets-page">
      <div className="support-tickets-header">
        <h1>Support Ticket Management</h1>
        <div className="support-tickets-stats">
          <div className="stat-card">
            <div className="stat-value">{totalCount}</div>
            <div className="stat-label">Total Tickets</div>
          </div>
          <button onClick={handleExport} className="export-button">
            Export CSV
          </button>
        </div>
      </div>

      <div className="support-tickets-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by subject or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">
            Search
          </button>
        </form>

        <div className="advanced-filters">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="technical">Technical</option>
            <option value="billing">Billing</option>
            <option value="account">Account</option>
            <option value="general">General</option>
            <option value="booking_issue">Booking Issue</option>
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => {
              setPriorityFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-select"
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>

          <button onClick={handleClearFilters} className="clear-filters-button">
            Clear Filters
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading support tickets...</div>
      ) : (
        <>
          <Table
            columns={columns}
            data={tickets}
            getRowId={(row) => row.ticketId}
            emptyMessage="No support tickets found"
            containerClassName="support-tickets-table-container"
            className="support-tickets-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || tickets.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {viewingTicket && (
        <TicketDetailModal ticketId={viewingTicket} onClose={closeTicketModal} />
      )}
    </div>
  );
};

const TicketDetailModal: React.FC<{ ticketId: string; onClose: () => void }> = ({ ticketId, onClose }) => {
  const [ticket, setTicket] = useState<SupportTicketWithUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [replying, setReplying] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on ticketId change only
  }, [ticketId]);

  const loadTicket = async () => {
    try {
      setLoading(true);
      const data = await supportTicketManagementService.getSupportTicket(ticketId);
      setTicket(data);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to load ticket');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) {
      alert('Please enter a message');
      return;
    }

    try {
      setReplying(true);
      await supportTicketManagementService.replyToTicket(ticketId, replyMessage);
      setReplyMessage('');
      await loadTicket();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleUpdateStatus = async (status: string) => {
    try {
      setUpdating(true);
      await supportTicketManagementService.updateTicket(ticketId, { status: status as SupportTicket['status'] });
      await loadTicket();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdatePriority = async (priority: string) => {
    try {
      setUpdating(true);
      await supportTicketManagementService.updateTicket(ticketId, { priority: priority as SupportTicket['priority'] });
      await loadTicket();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update priority');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div>Loading ticket...</div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content ticket-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ticket-header">
          <h2>Ticket #{ticket?.ticketId ? ticket.ticketId.substring(0, 8) : ticketId.substring(0, 8)}</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div className="ticket-info">
          <div className="info-row">
            <div className="info-item">
              <label>User</label>
              <div>{ticket.user?.email || ticket.userId}</div>
            </div>
            <div className="info-item">
              <label>Type</label>
              <div>{ticket.type}</div>
            </div>
            <div className="info-item">
              <label>Status</label>
              <select
                value={ticket.status}
                onChange={(e) => handleUpdateStatus(e.target.value)}
                disabled={updating}
                className="status-select"
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="info-item">
              <label>Priority</label>
              <select
                value={ticket.priority}
                onChange={(e) => handleUpdatePriority(e.target.value)}
                disabled={updating}
                className="priority-select"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
        </div>

        <div className="ticket-content">
          <div className="ticket-section">
            <h3>Subject</h3>
            <p>{ticket.subject}</p>
          </div>

          <div className="ticket-section">
            <h3>Description</h3>
            <p>{ticket.description}</p>
          </div>

          <div className="ticket-section">
            <h3>Conversation</h3>
            <div className="messages-list">
              {ticket.messages && ticket.messages.length > 0 ? (
                ticket.messages.map((msg: SupportTicketMessage) => (
                  <div key={msg.messageId} className={`message ${msg.isAdmin ? 'admin-message' : 'user-message'}`}>
                    <div className="message-header">
                      <span className="message-author">{msg.isAdmin ? 'Admin' : 'User'}</span>
                      <span className="message-time">
                        {new Date(msg.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="message-content">{msg.message}</div>
                  </div>
                ))
              ) : (
                <div className="no-messages">No messages yet</div>
              )}
            </div>
          </div>

          <div className="ticket-section">
            <h3>Reply</h3>
            <textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder="Type your reply here..."
              className="reply-textarea"
              rows={4}
            />
            <button
              onClick={handleReply}
              disabled={replying || !replyMessage.trim()}
              className="reply-button"
            >
              {replying ? 'Sending...' : 'Send Reply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

